import React, { useEffect } from 'react';
import { Container } from 'reactstrap';
import { Helmet } from 'react-helmet';
import { observer } from 'mobx-react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
    Block,
    FlexDiv,
    palette,
    StatusBadge,
    TextDefault,
    ThemedButton,
} from '@awarego-ui/components';
import HeaderSection from '../../components/header/HeaderSection';
import SubHeaderText from '../../components/header/SubHeaderText';
import SubHeaderComponentsList from '../../components/header/SubHeaderComponentsList';
import { useStores } from '../../hooks/use-stores';
import AwareExposeSubNav from './AwareExposeSubNav';

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
    font-size: 13px;

    th {
        text-align: left;
        font-size: 10px;
        font-weight: 600;
        color: ${palette.graphite.charcoal};
        text-transform: uppercase;
        letter-spacing: 0.05em;
        padding: 9px 12px;
        border-bottom: 1px solid ${palette.graphite.silver};
    }
    td {
        padding: 12px 12px;
        border-bottom: 1px solid ${palette.graphite.cloud};
        color: ${palette.graphite.heavyMetal};
        vertical-align: middle;
    }
    tr:hover td {
        background: ${palette.graphite.cloud};
    }
`;

function scoreColor(score) {
    if (score == null) return palette.graphite.charcoal;
    if (score >= 70) return palette.vibrant.red;
    if (score >= 50) return palette.vibrant.orange;
    return palette.evergreen.aspargus;
}

function statusBadge(status) {
    if (status === 'active')
        return (
            <StatusBadge
                text="In progress"
                color={palette.vibrant.orange}
            />
        );
    if (status === 'completed')
        return (
            <StatusBadge
                text="Complete"
                color={palette.evergreen.aspargus}
            />
        );
    if (status === 'cancelled')
        return (
            <StatusBadge
                text="Cancelled"
                color={palette.graphite.charcoal}
            />
        );
    return (
        <StatusBadge text="Draft" color={palette.graphite.charcoal} />
    );
}

function AwareExposeCampaigns() {
    const { store, awareExposeStore } = useStores();
    const navigate = useNavigate();
    const { currentCompany } = store;
    const { campaigns, loadingCampaigns } = awareExposeStore;

    useEffect(() => {
        if (currentCompany && currentCompany.company_id) {
            awareExposeStore.loadCampaigns(currentCompany.company_id);
        }
    }, [currentCompany && currentCompany.company_id]);

    return (
        <Container className="manager-content">
            <Helmet>
                <title>AwareExpose campaigns</title>
            </Helmet>
            <HeaderSection
                heading={'AwareExpose campaigns'}
                subHeadings={
                    <SubHeaderComponentsList>
                        <SubHeaderText
                            text={'Manage exposure check campaigns for your organisation.'}
                        />
                    </SubHeaderComponentsList>
                }
                buttons={
                    <ThemedButton
                        text="+ New campaign"
                        mode={'primary'}
                        onClick={() =>
                            navigate('/aware-expose/campaigns/new')
                        }
                        $noWrap
                    />
                }
            />
            <AwareExposeSubNav active="campaigns" />

            <Block tablePadding column>
                {campaigns.length === 0 ? (
                    <FlexDiv padding="24" center>
                        <TextDefault lighter>
                            {loadingCampaigns
                                ? 'Loading…'
                                : 'No campaigns yet — click "+ New campaign" to get started.'}
                        </TextDefault>
                    </FlexDiv>
                ) : (
                    <Table>
                        <thead>
                            <tr>
                                <th>Campaign</th>
                                <th>Created</th>
                                <th>Launched</th>
                                <th>Audience</th>
                                <th>Completion</th>
                                <th>Avg score</th>
                                <th>High risk</th>
                                <th>Status</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {campaigns.map((c) => {
                                const completion =
                                    c.invites_count > 0
                                        ? Math.round(
                                              (c.completed_count /
                                                  c.invites_count) *
                                                  100
                                          )
                                        : 0;
                                return (
                                    <tr key={c.id}>
                                        <td
                                            style={{
                                                fontWeight: 500,
                                                color:
                                                    palette.graphite.onyx,
                                            }}
                                        >
                                            <Link
                                                to={`/aware-expose/campaigns/${c.id}`}
                                                style={{
                                                    color:
                                                        palette.graphite
                                                            .onyx,
                                                }}
                                            >
                                                {c.name}
                                            </Link>
                                        </td>
                                        <td>
                                            {c.created_at
                                                ? new Date(
                                                      c.created_at
                                                  ).toLocaleDateString()
                                                : '—'}
                                        </td>
                                        <td>
                                            {c.launched_at
                                                ? new Date(
                                                      c.launched_at
                                                  ).toLocaleDateString()
                                                : '—'}
                                        </td>
                                        <td>{c.invites_count || 0}</td>
                                        <td>
                                            {c.invites_count
                                                ? `${completion}%`
                                                : '—'}
                                        </td>
                                        <td>
                                            <span
                                                style={{
                                                    color: scoreColor(
                                                        c.avg_score
                                                    ),
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {c.avg_score == null
                                                    ? '—'
                                                    : c.avg_score}
                                            </span>
                                        </td>
                                        <td>{c.high_risk_count || 0}</td>
                                        <td>{statusBadge(c.status)}</td>
                                        <td>
                                            <Link
                                                to={`/aware-expose/campaigns/${c.id}`}
                                                style={{ fontSize: 12 }}
                                            >
                                                View
                                            </Link>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </Table>
                )}
            </Block>
        </Container>
    );
}

export default observer(AwareExposeCampaigns);
